import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Swords, ArrowRight, Star } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';

export default function MatchupDashboard() {
    const [playerData, setPlayerData] = useState({ batters: [], bowlers: [] });
    const [batterId, setBatterId] = useState('');
    const [bowlerId, setBowlerId] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        API.get('/analytics/matchups/all').then(res => setPlayerData(res.data));
    }, []);

    const analyzeMatchup = async () => {
        if (!batterId || !bowlerId) return;
        setLoading(true);
        try {
            const res = await API.get(`/analytics/matchup?batter_id=${batterId}&bowler_id=${bowlerId}`);
            setResult(res.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    return (
        <div className="space-y-6" data-testid="matchup-dashboard">
            <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                    <Swords className="w-5 h-5 text-orange-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">MATCHUP INTELLIGENCE</h2>
                    <div className="badge-ai">HEAD TO HEAD</div>
                </div>
                <p className="text-sm text-white/40 mb-6">Analyze batter vs bowler matchups using historical data, form, and statistical modeling.</p>
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
                    <div className="flex-1 w-full">
                        <div className="tech-mono text-[10px] text-white/30 mb-2">SELECT BATTER</div>
                        <Select value={batterId} onValueChange={setBatterId}>
                            <SelectTrigger data-testid="batter-selector" className="bg-black/50 border-white/10 text-white h-12">
                                <SelectValue placeholder="Choose batter..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-white/10">
                                {playerData.batters?.map((p, idx) => (
                                    <SelectItem key={`bat-${p.id}-${idx}`} value={p.id} className="text-white hover:bg-white/10">
                                        {p.name} ({p.team_id.toUpperCase()})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10">
                        <Swords className="w-5 h-5 text-white/20" />
                    </div>
                    <div className="flex-1 w-full">
                        <div className="tech-mono text-[10px] text-white/30 mb-2">SELECT BOWLER</div>
                        <Select value={bowlerId} onValueChange={setBowlerId}>
                            <SelectTrigger data-testid="bowler-selector" className="bg-black/50 border-white/10 text-white h-12">
                                <SelectValue placeholder="Choose bowler..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-white/10">
                                {playerData.bowlers?.map((p, idx) => (
                                    <SelectItem key={`bowl-${p.id}-${idx}`} value={p.id} className="text-white hover:bg-white/10">
                                        {p.name} ({p.team_id.toUpperCase()})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button data-testid="analyze-matchup-btn" onClick={analyzeMatchup} disabled={loading || !batterId || !bowlerId}
                        className="h-12 bg-orange-500 text-white font-bold uppercase tracking-wide hover:bg-orange-400 shadow-[0_0_20px_rgba(249,115,22,0.3)]">
                        {loading ? 'Analyzing...' : <>Analyze <ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>
                </div>
            </div>

            {result && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Advantage Card */}
                    <div className="lg:col-span-12 glass-card p-6">
                        <div className="flex items-center justify-center flex-wrap gap-8">
                            <div className="text-center">
                                <div className="heading-primary text-lg text-cyan-400">{result.batter}</div>
                                <div className="tech-mono text-[10px] text-white/30">BATTER</div>
                                <div className="heading-primary text-5xl text-white mt-2">{result.batter_score}</div>
                            </div>
                            <div className="text-center px-8">
                                <div className={`heading-primary text-2xl ${result.advantage === 'Batter' ? 'text-cyan-400' : 'text-pink-400'}`}>
                                    {result.advantage === 'Batter' ? '<< BATTER ADVANTAGE' : 'BOWLER ADVANTAGE >>'}
                                </div>
                                <div className="heading-primary text-4xl text-yellow-400 mt-1">{result.advantage_percentage}%</div>
                                <div className="tech-mono text-[10px] text-white/30 mt-1">ADVANTAGE GAP</div>
                            </div>
                            <div className="text-center">
                                <div className="heading-primary text-lg text-pink-400">{result.bowler}</div>
                                <div className="tech-mono text-[10px] text-white/30">BOWLER</div>
                                <div className="heading-primary text-5xl text-white mt-2">{result.bowler_score}</div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Stats */}
                    <div className="lg:col-span-6 glass-card p-6">
                        <h3 className="heading-primary text-sm text-white mb-4">MATCHUP METRICS</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                                <span className="text-sm text-white/60">Expected Strike Rate</span>
                                <span className="heading-primary text-lg text-cyan-400">{result.expected_strike_rate}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                                <span className="text-sm text-white/60">Dismissal Probability</span>
                                <span className={`heading-primary text-lg ${result.dismissal_probability > 15 ? 'text-red-400' : 'text-green-400'}`}>{result.dismissal_probability}%</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                                <span className="text-sm text-white/60">Batter Rating</span>
                                <span className="heading-primary text-lg text-white">{result.batter_score}</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                                <span className="text-sm text-white/60">Bowler Rating</span>
                                <span className="heading-primary text-lg text-white">{result.bowler_score}</span>
                            </div>
                        </div>
                    </div>

                    {/* Visual Comparison */}
                    <div className="lg:col-span-6 glass-card p-6">
                        <h3 className="heading-primary text-sm text-white mb-4">POWER COMPARISON</h3>
                        <div className="space-y-6">
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span className="tech-mono text-xs text-cyan-400">BATTER POWER</span>
                                    <span className="text-sm text-white">{result.batter_score}/100</span>
                                </div>
                                <div className="h-4 rounded-full bg-white/5 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-cyan-600 to-cyan-400 transition-all duration-1000"
                                        style={{ width: `${result.batter_score}%` }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span className="tech-mono text-xs text-pink-400">BOWLER POWER</span>
                                    <span className="text-sm text-white">{result.bowler_score}/100</span>
                                </div>
                                <div className="h-4 rounded-full bg-white/5 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-pink-600 to-pink-400 transition-all duration-1000"
                                        style={{ width: `${result.bowler_score}%` }} />
                                </div>
                            </div>
                            <div className="p-4 bg-white/5 rounded-lg mt-4">
                                <div className="tech-mono text-[10px] text-white/30 mb-2">AI VERDICT</div>
                                <div className="text-sm text-white/80">
                                    {result.advantage === 'Batter'
                                        ? `${result.batter} holds the advantage in this matchup with an expected strike rate of ${result.expected_strike_rate}. The bowler has a ${result.dismissal_probability}% chance of taking the wicket per encounter.`
                                        : `${result.bowler} dominates this matchup. The batter's expected strike rate drops to ${result.expected_strike_rate} with a ${result.dismissal_probability}% dismissal probability per encounter.`
                                    }
                                </div>
                            </div>

                            {/* Impact Player Insight */}
                            {(result.batter_impact_effect || result.bowler_impact_effect) && (
                                <div className="p-4 bg-orange-500/5 border border-orange-500/10 rounded-lg mt-3" data-testid="matchup-impact-insight">
                                    <div className="flex items-center gap-1 mb-2">
                                        <Star className="w-3 h-3 text-orange-400" />
                                        <span className="tech-mono text-[10px] text-orange-400 font-bold">IMPACT PLAYER INSIGHT</span>
                                    </div>
                                    <div className="space-y-1.5 text-xs">
                                        {result.batter_impact_effect && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-white/60">{result.batter} as batting impact</span>
                                                <span className="text-green-400 font-bold">+{result.batter_impact_effect.win_pct_shift}% win | +{result.batter_impact_effect.score_impact} runs</span>
                                            </div>
                                        )}
                                        {result.bowler_impact_effect && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-white/60">{result.bowler} as bowling impact</span>
                                                <span className="text-blue-400 font-bold">+{result.bowler_impact_effect.win_pct_shift}% win | {result.bowler_impact_effect.score_impact} runs</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
